/**
 * The landing page's two offline-distribution sections (plan §3.2, §3.7):
 *
 *  - Download: the latest stable (or beta) package with its size, sha256 and
 *    notes, gated by hospital code + download PIN, plus the manual path;
 *  - Check my version: asks the hospital's own server on the LAN what it
 *    runs and compares that with the latest published release.
 *
 * Same visual system as the rest of `HomePage.tsx`: the shadcn-style tokens,
 * `Button`/`Badge`/`Input` primitives, `Reveal` for the scroll-in.
 */
import { useState, type FormEvent, type ReactNode } from 'react';
import { AlertTriangle, CheckCircle2, Download, ExternalLink, FileDown, KeyRound, MonitorCheck, Package, Terminal } from 'lucide-react';
import { compareVersions, ERROR_CODES, type LocalUpdateStatusDto, type PublicLandingDto, type ReleaseChannel, type UpdateLatestDto } from '@his/shared';
import { ApiError } from '@/lib/api';
import { formatBytes } from '@/lib/format-bytes';
import { cn, dateOnly, dateTime } from '@/lib/utils';
import { Badge, Button, Input } from '@/components/ui/primitives';
import { ReleaseNotes } from '@/components/ui/release-notes';
import { ABOUT_SETTINGS_ROUTE, CHECK_VERSION_COPY, DOWNLOAD_COPY, LOCAL_SERVER_ADDRESS_STORAGE_KEY, LOCAL_SERVER_DEFAULT_ADDRESS } from './home-data';
import { agentDownloadUrl, browserDownloadUrl, fetchLocalServerStatus, normalizeServerAddress, validateDownloadAccess } from './home-api';
import { Reveal } from './home-visuals';

function Heading({ eyebrow, title, lead }: { eyebrow: string; title: string; lead: string }) {
  return (
    <div className="max-w-2xl">
      <p className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-primary">
        <span className="h-1.5 w-1.5 rounded-full bg-primary" aria-hidden />
        {eyebrow}
      </p>
      <h2 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">{title}</h2>
      <p className="mt-4 text-base leading-7 text-muted-foreground">{lead}</p>
    </div>
  );
}

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="mt-1 text-sm">{children}</p>
    </div>
  );
}

function FieldLabel({ children }: { children: ReactNode }) {
  return <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">{children}</span>;
}

function ManualSteps() {
  return (
    <div className="rounded-lg border bg-card p-5">
      <h3 className="flex items-center gap-2 text-[15px] font-semibold">
        <Terminal className="h-4 w-4 text-warning" aria-hidden /> {DOWNLOAD_COPY.manualTitle}
      </h3>
      <ol className="mt-3 list-decimal space-y-2 ps-5 text-sm leading-6 text-muted-foreground">
        {DOWNLOAD_COPY.manualSteps.map((step) => (
          <li key={step} className="break-words">
            {step}
          </li>
        ))}
      </ol>
    </div>
  );
}

function DownloadForm({ release, channel }: { release: UpdateLatestDto; channel: ReleaseChannel }) {
  const [tenantSlug, setTenantSlug] = useState('');
  const [pin, setPin] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const canSubmit = tenantSlug.trim().length >= 2 && /^\d{6}$/.test(pin) && !busy;

  const submit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    const auth = { tenantSlug: tenantSlug.trim().toLowerCase(), pin };
    try {
      const latest = await validateDownloadAccess(auth, channel);
      window.location.assign(browserDownloadUrl(latest.version, auth));
    } catch (err) {
      if (err instanceof ApiError && err.status === 429) setError(DOWNLOAD_COPY.rateLimited);
      else if (err instanceof ApiError && err.status === 401) setError(DOWNLOAD_COPY.invalid);
      else if (err instanceof ApiError && err.status === 403 && err.code === ERROR_CODES.OFFLINE_NOT_ENABLED) setError(DOWNLOAD_COPY.offlineNotEnabled);
      else setError(err instanceof ApiError ? err.message : 'Could not start the download.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={(e) => void submit(e)} className="rounded-lg border border-primary/30 bg-card p-5" noValidate>
      <h3 className="flex items-center gap-2 text-[15px] font-semibold">
        <KeyRound className="h-4 w-4 text-primary" aria-hidden /> {DOWNLOAD_COPY.authTitle}
      </h3>
      <p className="mt-2 text-xs leading-5 text-muted-foreground">{DOWNLOAD_COPY.authHint}</p>
      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        <label className="block">
          <FieldLabel>{DOWNLOAD_COPY.tenantSlugLabel}</FieldLabel>
          <Input className="mt-1 font-mono lowercase" value={tenantSlug} autoComplete="organization" maxLength={40} onChange={(e) => setTenantSlug(e.target.value)} />
        </label>
        <label className="block">
          <FieldLabel>{DOWNLOAD_COPY.pinLabel}</FieldLabel>
          <Input
            className="mt-1 font-mono tracking-[0.3em]"
            value={pin}
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
          />
        </label>
      </div>
      {error && (
        <p className="mt-3 flex items-center gap-2 text-sm text-destructive">
          <AlertTriangle className="h-4 w-4 flex-none" aria-hidden /> {error}
        </p>
      )}
      <Button type="submit" className="mt-4 w-full" disabled={!canSubmit} loading={busy}>
        <Download className="h-4 w-4" aria-hidden /> {busy ? DOWNLOAD_COPY.verifying : `${DOWNLOAD_COPY.downloadButton} his-${release.version}.lpk`}
      </Button>
    </form>
  );
}

function ReleaseCard({
  release,
  channel,
  onSwitch,
  betaNewer,
}: {
  release: UpdateLatestDto;
  channel: ReleaseChannel;
  onSwitch: (c: ReleaseChannel) => void;
  betaNewer: boolean;
}) {
  return (
    <div className="rounded-lg border bg-card p-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="grid h-11 w-11 place-items-center rounded-md bg-primary/10 text-primary">
            <Package className="h-5 w-5" aria-hidden />
          </span>
          <div>
            <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">{channel === 'STABLE' ? DOWNLOAD_COPY.latestLabel : DOWNLOAD_COPY.betaLabel}</p>
            <p className="text-2xl font-semibold">
              v{release.version}
              {release.isRequired && (
                <Badge variant="destructive" className="ms-2 align-middle">
                  Required
                </Badge>
              )}
            </p>
          </div>
        </div>
        {(betaNewer || channel === 'BETA') && (
          <Button type="button" variant="outline" size="sm" onClick={() => onSwitch(channel === 'STABLE' ? 'BETA' : 'STABLE')}>
            {channel === 'STABLE' ? DOWNLOAD_COPY.betaLabel : DOWNLOAD_COPY.latestLabel}
          </Button>
        )}
      </div>
      {channel === 'STABLE' && betaNewer && <p className="mt-3 text-xs text-warning">{DOWNLOAD_COPY.betaHint}</p>}
      <div className="mt-5 grid grid-cols-2 gap-4 sm:grid-cols-3">
        <Fact label="Released">{dateOnly(release.publishedAt)}</Fact>
        <Fact label="Size">{formatBytes(release.size)}</Fact>
        <Fact label="Minimum version">{release.minimumAllowedVersion ?? '—'}</Fact>
      </div>
      <div className="mt-4">
        <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">SHA-256</p>
        <p className="mt-1 break-all font-mono text-[11px] leading-4 text-muted-foreground">{release.sha256}</p>
      </div>
      <div className="mt-5 border-t pt-4">
        <p className="mb-2 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Release notes</p>
        <div className="max-h-64 overflow-y-auto pe-1">
          <ReleaseNotes notes={release.notes} />
        </div>
      </div>
    </div>
  );
}

function AgentsList({ agents }: { agents: PublicLandingDto['agents'] }) {
  return (
    <div className="rounded-lg border bg-card p-5">
      <h3 className="text-[15px] font-semibold">{DOWNLOAD_COPY.agentsTitle}</h3>
      <p className="mt-2 text-xs leading-5 text-muted-foreground">{DOWNLOAD_COPY.agentsLead}</p>
      {agents.length === 0 ? (
        <p className="mt-4 text-sm text-muted-foreground">{DOWNLOAD_COPY.agentsEmpty}</p>
      ) : (
        <ul className="mt-4 divide-y">
          {agents.map((agent) => (
            <li key={agent.fileName} className="flex items-center justify-between gap-3 py-2.5">
              <div className="min-w-0">
                <p className="truncate font-mono text-sm">{agent.name}</p>
                <p className="text-[11px] text-muted-foreground">
                  {agent.version ? `v${agent.version} · ` : ''}
                  {formatBytes(agent.size)}
                </p>
              </div>
              <a href={agentDownloadUrl(agent.fileName)} download className="inline-flex flex-none items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-medium hover:bg-accent">
                <FileDown className="h-3.5 w-3.5" aria-hidden /> Download
              </a>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function DownloadSection({ landing, loading }: { landing: PublicLandingDto | undefined; loading: boolean }) {
  const [channel, setChannel] = useState<ReleaseChannel>('STABLE');
  const stable = landing?.latestStable ?? null;
  const beta = landing?.latestBeta ?? null;
  const betaNewer = stable !== null && beta !== null ? compareVersions(beta.version, stable.version) > 0 : beta !== null && stable === null;
  const release = channel === 'BETA' && beta ? beta : (stable ?? beta);
  const shownChannel: ReleaseChannel = release === beta && beta !== null && release !== stable ? 'BETA' : 'STABLE';

  return (
    <section id="download" className="scroll-mt-16 border-t bg-muted/30 py-20 sm:py-24">
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        <Heading eyebrow={DOWNLOAD_COPY.eyebrow} title={DOWNLOAD_COPY.title} lead={DOWNLOAD_COPY.lead} />
        <div className="mt-12 grid gap-6 lg:grid-cols-[1.15fr_0.85fr]">
          <div className="space-y-6">
            {loading ? (
              <div className="h-72 animate-pulse rounded-lg border bg-muted/40" />
            ) : release ? (
              <Reveal>
                <ReleaseCard release={release} channel={shownChannel} betaNewer={betaNewer} onSwitch={setChannel} />
              </Reveal>
            ) : (
              <div className="rounded-lg border bg-card p-6 text-sm text-muted-foreground">{DOWNLOAD_COPY.noRelease}</div>
            )}
            {landing && (
              <Reveal delay={80}>
                <AgentsList agents={landing.agents ?? []} />
              </Reveal>
            )}
          </div>
          <div className="space-y-6">
            {release && (
              <Reveal delay={60}>
                <DownloadForm release={release} channel={shownChannel} />
              </Reveal>
            )}
            <Reveal delay={120}>
              <ManualSteps />
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}

function readStoredAddress(): string {
  try {
    return window.localStorage.getItem(LOCAL_SERVER_ADDRESS_STORAGE_KEY) ?? LOCAL_SERVER_DEFAULT_ADDRESS;
  } catch {
    return LOCAL_SERVER_DEFAULT_ADDRESS;
  }
}

function storeAddress(address: string): void {
  try {
    window.localStorage.setItem(LOCAL_SERVER_ADDRESS_STORAGE_KEY, address);
  } catch {
    return;
  }
}

type CheckResult = { kind: 'ok'; status: LocalUpdateStatusDto; address: string } | { kind: 'unreachable' } | null;

export function CheckVersionSection({ landing }: { landing: PublicLandingDto | undefined }) {
  const [address, setAddress] = useState(readStoredAddress);
  const [checking, setChecking] = useState(false);
  const [result, setResult] = useState<CheckResult>(null);
  const latest = landing?.latestStable ?? null;

  const check = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    const normalized = normalizeServerAddress(address);
    if (!normalized) {
      setResult({ kind: 'unreachable' });
      return;
    }
    setChecking(true);
    storeAddress(normalized);
    try {
      const status = await fetchLocalServerStatus(normalized);
      setResult({ kind: 'ok', status, address: normalized });
    } catch {
      setResult({ kind: 'unreachable' });
    } finally {
      setChecking(false);
    }
  };

  const outdated = result?.kind === 'ok' && latest !== null && compareVersions(result.status.runningVersion, latest.version) < 0;
  const pendingOnServer = result?.kind === 'ok' ? result.status.pendingUpdate : null;

  return (
    <section id="check-version" className="scroll-mt-16 border-t py-20 sm:py-24">
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        <div className="grid gap-12 lg:grid-cols-[0.9fr_1.1fr]">
          <Heading eyebrow={CHECK_VERSION_COPY.eyebrow} title={CHECK_VERSION_COPY.title} lead={CHECK_VERSION_COPY.lead} />
          <div className="space-y-4">
            <form onSubmit={(e) => void check(e)} className="rounded-lg border bg-card p-5" noValidate>
              <label className="block">
                <FieldLabel>{CHECK_VERSION_COPY.addressLabel}</FieldLabel>
                <div className="mt-1 flex gap-2">
                  <Input className="font-mono" value={address} placeholder={CHECK_VERSION_COPY.addressPlaceholder} onChange={(e) => setAddress(e.target.value)} />
                  <Button type="submit" className="flex-none" loading={checking}>
                    <MonitorCheck className="h-4 w-4" aria-hidden /> {checking ? CHECK_VERSION_COPY.checking : CHECK_VERSION_COPY.button}
                  </Button>
                </div>
              </label>
            </form>

            {result?.kind === 'ok' && (
              <div className={cn('rounded-lg border bg-card p-5', outdated ? 'border-warning/40' : 'border-success/40')}>
                <div className="flex items-center gap-2">
                  {outdated ? <AlertTriangle className="h-5 w-5 text-warning" aria-hidden /> : <CheckCircle2 className="h-5 w-5 text-success" aria-hidden />}
                  <p className="text-lg font-semibold">{outdated && latest ? `Version ${latest.version} available` : CHECK_VERSION_COPY.upToDate}</p>
                  {result.status.lockState !== 'NONE' && (
                    <Badge variant="destructive" className="ms-auto">
                      {CHECK_VERSION_COPY.lockedLabel}
                    </Badge>
                  )}
                </div>
                <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-3">
                  <Fact label={CHECK_VERSION_COPY.runningLabel}>
                    v{result.status.runningVersion}
                    {result.status.channel === 'BETA' ? ' (beta)' : ''}
                  </Fact>
                  <Fact label={CHECK_VERSION_COPY.latestLabel}>{latest ? `v${latest.version}` : '—'}</Fact>
                  <Fact label="Last check-in">{result.status.lastCheckInAt ? dateTime(result.status.lastCheckInAt) : 'Never'}</Fact>
                </div>
                {outdated && (
                  <div className="mt-5 space-y-2 border-t pt-4">
                    <a href={`${result.address}${ABOUT_SETTINGS_ROUTE}`} target="_blank" rel="noreferrer">
                      <Button type="button">
                        <ExternalLink className="h-4 w-4" aria-hidden /> {CHECK_VERSION_COPY.openAbout}
                      </Button>
                    </a>
                    <p className="text-xs leading-5 text-muted-foreground">
                      {CHECK_VERSION_COPY.openAboutHint}
                      {pendingOnServer === null ? ' The server has not yet noticed the new release — press "Check for updates now" on that screen first.' : ''}
                    </p>
                  </div>
                )}
              </div>
            )}

            {result?.kind === 'unreachable' && (
              <div className="rounded-lg border border-warning/40 bg-card p-5">
                <p className="flex items-center gap-2 text-[15px] font-semibold">
                  <AlertTriangle className="h-4 w-4 text-warning" aria-hidden /> {CHECK_VERSION_COPY.unreachableTitle}
                </p>
                <p className="mt-2 text-sm leading-6 text-muted-foreground">{CHECK_VERSION_COPY.unreachableHint}</p>
                <ol className="mt-3 list-decimal space-y-1.5 ps-5 text-sm leading-6 text-muted-foreground">
                  {DOWNLOAD_COPY.manualSteps.map((step) => (
                    <li key={step} className="break-words">
                      {step}
                    </li>
                  ))}
                </ol>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
